import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import TableComponent from './Tablecomponent';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Button, FormControl, InputLabel, MenuItem, Paper, Select } from '@mui/material';
import labels from '../../img/labels.svg'
import SearchBox from '../../components/reusableComponents/SearchBox';
import ApiService from '../../_services/ApiService';
import { setLoader } from '../../redux';
import dayjs from 'dayjs';

// studio-list rows expose the display name as studio_name / name / user_handle
// (same fallback order StudioTable uses).
const studioDisplayName = (s) => s?.studio_name || s?.name || s?.user_handle || '';

// Distinct background/text colors per payout status so chips aren't all yellow.
const getStatusChipStyle = (status) => {
    switch (status) {
        case 'Paid':
            return { backgroundColor: 'rgba(76, 175, 80, 0.18) !Important', color: '#2E7D32' };
        case 'On-Hold':
            return { backgroundColor: 'rgba(255, 152, 0, 0.2) !Important', color: '#E65100' };
        case 'Requested':
            return { backgroundColor: 'rgba(33, 150, 243, 0.18) !Important', color: '#0D47A1' };
        case 'Rejected':
            return { backgroundColor: 'rgba(244, 67, 54, 0.18) !Important', color: '#B71C1C' };
        default:
            return { backgroundColor: 'rgba(0, 0, 0, 0.08) !Important', color: 'rgba(58, 58, 58, 1)' };
    }
};

export default function PayoutList({ hideTotal }) {
    const dispatch = useDispatch();
    const history = useHistory();
    const [page, setPage] = useState(() => Number(sessionStorage.getItem('payoutlist_page')) || 0);
    const [rowsPerPage, setRowsPerPage] = useState(() => Number(sessionStorage.getItem('payoutlist_rowsPerPage')) || 10);
    const [rows, setRows] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [studioNameMap, setStudioNameMap] = useState(null);
    const [searchInput, setSearchInput] = useState(() => sessionStorage.getItem('payoutlist_search') || '');
    const [statusFilter, setStatusFilter] = useState(() => sessionStorage.getItem('payoutlist_status') || '');

    // Persist list view so returning from the detail page keeps the same
    // page, rows-per-page, search and status filter.
    useEffect(() => {
        sessionStorage.setItem('payoutlist_page', page);
        sessionStorage.setItem('payoutlist_rowsPerPage', rowsPerPage);
        if (searchInput) sessionStorage.setItem('payoutlist_search', searchInput);
        else sessionStorage.removeItem('payoutlist_search');
        if (statusFilter) sessionStorage.setItem('payoutlist_status', statusFilter);
        else sessionStorage.removeItem('payoutlist_status');
    }, [page, rowsPerPage, searchInput, statusFilter]);

    const viewDetails = (row) => {
        history.push('/payout-detail', { state: { data: row } })
    }

    // One studio-list call → { studio_id: studio_name } map for resolving
    // each payout row's publisher_id to a readable customer name.
    const buildStudioNameMap = async () => {
        try {
            const res = await ApiService.get('studioTableList', { limit: 1000, offset: 0 });
            const studios = res?.data?.results || [];
            return studios.reduce((acc, s) => {
                if (s?.studio_id != null) acc[s.studio_id] = studioDisplayName(s);
                return acc;
            }, {});
        } catch (e) {
            return {};
        }
    };

    const getPayoutList = async () => {
        dispatch(setLoader(true));
        try {
            const params = { limit: rowsPerPage, offset: page };
            if (searchInput) params.search = searchInput;
            if (statusFilter) params.status = statusFilter;
            const res = await ApiService.get('payout_status', params);
            const results = res?.data?.results || [];
            setTotalCount(res?.data?.total_count || 0);

            let nameMap = studioNameMap;
            if (!nameMap) {
                nameMap = await buildStudioNameMap();
                setStudioNameMap(nameMap);
            }

            setRows(results.map((item) => ({
                id: item.id,
                raw: item,
                customer: nameMap[item.publisher_id] || `Publisher #${item.publisher_id}`,
                payment_type: item.payout_type || '-',
                requested_date: item.requested_date ? dayjs(item.requested_date).format('D MMM, YYYY hh:mm a') : '-',
                payment_date: item.paid_on
                    ? dayjs(item.paid_on).format('D MMM, YYYY hh:mm a')
                    : (item.payment_date ? dayjs(item.payment_date).format('D MMM, YYYY') : '-'),
                requested_amt: item.amount_paid ?? '-',
                status: item.status || '-',
            })));
        } catch (error) {
            console.error('Error fetching payout list:', error);
        } finally {
            dispatch(setLoader(false));
        }
    };

    useEffect(() => {
        const t = setTimeout(() => getPayoutList(), searchInput ? 400 : 0);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage, searchInput, statusFilter]);

    const handleSearchInput = (value) => {
        setSearchInput(value);
        setPage(0);
        if (value) sessionStorage.setItem('payoutlist_search', value);
        else sessionStorage.removeItem('payoutlist_search');
        sessionStorage.setItem('payoutlist_page', 0);
    };

    const handleStatusChange = (e) => {
        const value = e.target.value;
        setStatusFilter(value);
        setPage(0);
        if (value) sessionStorage.setItem('payoutlist_status', value);
        else sessionStorage.removeItem('payoutlist_status');
        sessionStorage.setItem('payoutlist_page', 0);
    };

    const columns = [
        {
            id: 'customer', label: 'CUSTOMER', headerName: '', template: (row) => {
                return (
                    <Box className='d-flex gap-2 align-items-center' ><img src={labels} alt='label-icon' />{row.customer}</Box>
                )
            }
        },
        { label: 'PAYMENT TYPE', field: "payment_type", id: 'payment_type' },
        {
            id: 'requested_date', label: 'REQUESTED DATE & TIME', headerName: '',
            template: (row) => <Box sx={{ whiteSpace: 'nowrap' }}>{row.requested_date}</Box>
        },
        {
            id: 'payment_date', label: 'PAYMENT DATE & TIME', headerName: '',
            template: (row) => <Box sx={{ whiteSpace: 'nowrap' }}>{row.payment_date}</Box>
        },
        { label: 'REQUESTED AMOUNT', field: "requested_amt", id: 'requested_amt' },
        {
            id: 'status', label: 'STATUS', headerName: '', flex: 1, template: (row) => {
                return (
                    <Button className='text-capitalize rounded-pill px-3' sx={{ ...getStatusChipStyle(row.status), fontSize: 12, whiteSpace: 'nowrap', minWidth: 'auto' }}>{row.status}</Button>
                )
            }
        },
    ];

    return (
        <Box className="py-3 px-4">
            <h2 style={{ marginLeft: '10px' }}>Payout</h2>
            <Box sx={{ mt: 2, ml: '10px', display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <Box sx={{ maxWidth: 320, flex: '1 1 240px' }}>
                    <SearchBox placeholder="Search payouts" searchValue={searchInput} handleSearchInput={handleSearchInput} />
                </Box>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel id="payout-status-filter-label">Status</InputLabel>
                    <Select
                        labelId="payout-status-filter-label"
                        label="Status"
                        value={statusFilter}
                        onChange={handleStatusChange}
                    >
                        <MenuItem value="Requested">Requested</MenuItem>
                        <MenuItem value="Paid">Paid</MenuItem>
                        <MenuItem value="On-Hold">On-Hold</MenuItem>
                    </Select>
                </FormControl>
            </Box>
            <Paper sx={{ width: '100%', mb: 2, mt: 3 }}>
                <TableComponent removeStyle='removeStyle' columns={columns} data={rows} totalCount={totalCount} page={page} setPage={setPage} rowsPerPage={rowsPerPage} setRowsPerPage={setRowsPerPage} onRowClick={viewDetails} />
            </Paper>
        </Box>
    );
}
